// Single source of truth for the Anxiety Maps section.
//
// Each map is a curated visual cluster: a central anxiety concept with spokes
// to the existing AnxietyResource article on each related experience. The data
// here drives the hub page, the six map pages, the homepage Maps section, the
// nav, and the sitemap. Node hrefs are real, published routes (verified against
// the content inventory). Where the spec named a slug that does not exist, the
// closest published entry is used; substitutions are documented in the build
// report, not here.

export const MAPS_REVIEW_ISO = "2026-06-22";
export const MAPS_REVIEW_DISPLAY = "June 22, 2026";

export const MAPS_SUPPORT_EMAIL = "support@anxietyresource.org";

export interface MapNode {
  /** Spoke label, e.g. "Heart palpitations". */
  label: string;
  /** One-line description shown in the "Inside this cluster" list. */
  desc: string;
  /** Destination route or absolute URL. */
  href: string;
  /** True when href is an external network property (opens in a new tab). */
  external?: boolean;
}

export interface AnxietyMapData {
  /** URL slug under /maps/. */
  slug: string;
  /** Page H1, e.g. "The Panic Map". */
  h1: string;
  /** Short map name used in nav, breadcrumb, preview cards, e.g. "Panic Map". */
  name: string;
  /** Central node label inside the green hub circle. */
  central: string;
  /** <title> tag text. */
  pageTitle: string;
  /** Meta description (verbatim, section 8). */
  metaDescription: string;
  /** One-line subhead under the H1. */
  subhead: string;
  /** Lede paragraphs. */
  lede: string[];
  /** Spokes radiating from the central node. */
  nodes: MapNode[];
  /** "Why this cluster matters" closing paragraph. */
  whyClusterMatters: string;
  /** network_topics passed to NetworkRecommendations. */
  networkTopics: string[];
  /** One-line description for the hub / homepage preview cards. */
  previewDesc: string;
  /** "Treatment that works on this cluster" paragraph. */
  treatment: string;
  /** "What people describe" vignette paragraphs (two). */
  whatPeopleDescribe: string[];
}

function altFor(map: { central: string; name: string; nodes: MapNode[] }): string {
  const labels = map.nodes.map((n) => n.label.toLowerCase());
  const last = labels.pop();
  const list = labels.join(", ") + ", and " + last;
  return `${map.name}: a central node labeled ${map.central} connected to ${map.nodes.length} related experiences: ${list}.`;
}

export const MAPS: AnxietyMapData[] = [
  {
    slug: "panic",
    h1: "The Panic Map",
    name: "Panic Map",
    central: "Panic",
    pageTitle: "Panic Map | AnxietyResource",
    metaDescription:
      "A visual map of panic: heart palpitations, shortness of breath, dizziness, derealization, sensitization, catastrophic thoughts, chest tightness, and the avoidance loop. Psychiatrist-reviewed.",
    subhead:
      "The cluster of experiences inside a panic attack and the loop that turns one attack into many.",
    lede: [
      "A panic attack is rarely one thing. It's a cluster. A surge of physical sensations arrives with a flood of catastrophic thoughts, and the two reinforce each other in seconds. Most people remember a panic attack as a single overwhelming event, but the inside of it is more like a circle. The body shifts, the mind reads the body shift as proof of disaster, the disaster thought spikes the body further, and the loop tightens.",
      "This map is the cluster, laid out. Most people are surprised to see how many of these experiences are already familiar; they didn't know they belonged to the same picture.",
    ],
    nodes: [
      {
        label: "Heart palpitations",
        desc: "A racing or pounding heart, often the first thing people notice.",
        href: "/symptoms/heart-palpitations",
      },
      {
        label: "Shortness of breath",
        desc: "The chest tightens and breath feels shallow, which feeds the body's alarm.",
        href: "/symptoms/shallow-breathing",
      },
      {
        label: "Dizziness",
        desc: "A spinning or untethered feeling, often from the breathing change.",
        href: "/symptoms/dizziness",
      },
      {
        label: "Derealization",
        desc: "The world feeling unreal or far away, your own voice sounding strange.",
        href: "/symptoms/derealization",
      },
      {
        label: "Sensitization",
        desc: "Past panic attacks make the nervous system more reactive to small sensations.",
        href: "/panic-nervous-system/sensitization",
      },
      {
        label: "Catastrophic thoughts",
        desc: "\"Something is wrong,\" \"I'm having a heart attack,\" \"I'm losing my mind.\"",
        href: "/overthinking-rumination/catastrophic-thinking",
      },
      {
        label: "Chest tightness",
        desc: "The classic squeezing or pressure feeling that scares people most.",
        href: "/symptoms/chest-tightness",
      },
      {
        label: "Avoidance",
        desc: "Steering around places, situations, or feelings where panic has happened before.",
        href: "/glossary/avoidance",
      },
    ],
    whyClusterMatters:
      "Once a person recognizes that all eight of these are part of the same loop, the loop loosens. Heart palpitations stop feeling like a separate medical emergency and start feeling like a familiar piece of a familiar cluster. Sensitization is the part most people miss; it's why panic feels like it's getting worse even though nothing in life has changed. The treatments that actually work, CBT with an exposure component, SSRIs, and learning that a panic attack always ends, work on the whole cluster, not on any one piece.",
    networkTopics: ["panic", "panic-disorder", "anxiety"],
    previewDesc:
      "Heart, breath, dizziness, derealization, sensitization, and the loop that turns one panic attack into many.",
    treatment:
      "Panic responds best to cognitive behavioral therapy with interoceptive exposure (deliberately, gradually re-entering the sensations the nervous system has learned to fear) combined when needed with an SSRI to lower the baseline. Benzodiazepines can ease an acute attack but aren't the long-term answer because they teach the nervous system it needs them. The combination of exposure and SSRI is what changes the underlying loop.",
    whatPeopleDescribe: [
      "One person describes lying in bed after the first attack and waiting for it to come back. Every twinge becomes a possible warning. They learn to monitor their heart rate during meetings, during workouts, during sex. The monitoring is exhausting, and it's also keeping the cluster alive: the body learns it should be watched, so the body keeps producing things to watch.",
      "Another describes the moment in the grocery store when the lights felt wrong. They left their cart and drove home. After that, the grocery store stayed on the list of places that 'do this to me.' Six months later, the list included three grocery stores, two restaurants, the gym, and most of the highway. The avoidance felt protective. It was also the cluster, expanding.",
    ],
  },
  {
    slug: "overthinking",
    h1: "The Overthinking Map",
    name: "Overthinking Map",
    central: "Overthinking",
    pageTitle: "Overthinking Map | AnxietyResource",
    metaDescription:
      "A visual map of overthinking: rumination, catastrophizing, perfectionism, reassurance-seeking, mental rehearsal, intrusive thoughts, and what-if loops. Psychiatrist-reviewed.",
    subhead:
      "The cluster of mental patterns that look like thinking but aren't, and the way they self-perpetuate.",
    lede: [
      "Overthinking gets called many things. Rumination. Catastrophizing. Reassurance-seeking. Mental rehearsal. Worry. They're not the same, but they share a structure: the mind treats certainty itself as the goal, and there is no amount of thinking that gets you there. So the thinking keeps running.",
      "This map shows how the pieces fit. People who recognize themselves in it often describe the relief of finally naming what their mind has been doing. The naming doesn't stop the loop, but it's where the loop starts to loosen.",
    ],
    nodes: [
      {
        label: "Rumination",
        desc: "Replaying the same conversation, decision, or mistake without resolving it.",
        href: "/overthinking-rumination/replaying-conversations",
      },
      {
        label: "Catastrophizing",
        desc: "Jumping to the worst-case outcome and treating it as the likely one.",
        href: "/overthinking-rumination/catastrophic-thinking",
      },
      {
        label: "Perfectionism",
        desc: "Setting a bar so high that any outcome feels like failure.",
        href: "/work-performance/perfectionism",
      },
      {
        label: "Reassurance-seeking",
        desc: "Asking the same question of partners, doctors, search engines, looking for relief that doesn't last.",
        href: "/overthinking-rumination/reassurance-seeking",
      },
      {
        label: "Mental rehearsal",
        desc: "Running tomorrow's conversation, today, eight times.",
        href: "/work-performance/sunday-night-anxiety",
      },
      {
        label: "Intrusive thoughts",
        desc: "Unwanted thoughts the person knows aren't them.",
        href: "/overthinking-rumination/intrusive-thoughts",
      },
      {
        label: "What-if loops",
        desc: "Each answer spawns the next question; the loop has no end.",
        href: "/conditions/generalized-anxiety-disorder",
      },
      {
        label: "Decision paralysis",
        desc: "Every option spawns a list of objections, so no decision gets made.",
        href: "/glossary/black-and-white-thinking",
      },
    ],
    whyClusterMatters:
      "Most popular advice for overthinking is to \"just stop thinking about it.\" It doesn't work, because the cluster isn't a thinking problem; it's a relationship-to-thinking problem. CBT and ACT both work on the cluster by changing what the person does with a thought, not by trying to stop the thought from arriving. shrinQ is built around exactly this work.",
    networkTopics: ["overthinking", "rumination", "anxiety"],
    previewDesc:
      "Rumination, perfectionism, catastrophizing, reassurance-seeking, and the mental rehearsals that look like thinking but aren't.",
    treatment:
      "Overthinking responds to cognitive behavioral therapy that targets rumination directly, often combined with mindfulness-based approaches that change the relationship to thought rather than the content of thought. SSRIs can help when overthinking is severe enough to disrupt daily life. The goal isn't to think less; it's to spend less time inside the loop.",
    whatPeopleDescribe: [
      "One person describes lying down to sleep and replaying a conversation from earlier in the day. Did they sound annoying? What did the pause mean? Should they text to clarify? They reach for their phone, then put it down. An hour passes. The conversation hasn't changed. They are still in it.",
      "Another describes the way decisions stall. Choosing a restaurant takes thirty minutes. Choosing a job takes a year. Each option generates ten branching what-ifs, and every what-if needs to be worked through before any choice can land. The thinking feels like rigor. It also feels like being trapped.",
    ],
  },
  {
    slug: "social-anxiety",
    h1: "The Social Anxiety Map",
    name: "Social Anxiety Map",
    central: "Social anxiety",
    pageTitle: "Social Anxiety Map | AnxietyResource",
    metaDescription:
      "A visual map of social anxiety: anticipatory dread, self-consciousness, avoidance, embarrassment sensitivity, post-event rumination, and the safety behaviors that keep it going. Psychiatrist-reviewed.",
    subhead:
      "The cluster of fears and rituals that make social situations cost more than they should.",
    lede: [
      "Social anxiety is rarely one fear. It's a sequence. Anticipatory dread before a social event; self-consciousness during it; rumination after it. Avoidance threads through all of it, narrowing life in small ways that add up. Once the cluster is named, the parts stop feeling like personal failings and start looking like a known pattern with known treatment.",
    ],
    nodes: [
      {
        label: "Anticipatory anxiety",
        desc: "The dread that builds in the days or hours before a social situation.",
        href: "/work-performance/sunday-night-anxiety",
      },
      {
        label: "Self-consciousness",
        desc: "The feeling of being watched and judged in real time.",
        href: "/relationships-social/social-anxiety-adults",
      },
      {
        label: "Avoidance",
        desc: "Saying no to invitations, leaving early, declining to speak up.",
        href: "/glossary/avoidance",
      },
      {
        label: "Embarrassment sensitivity",
        desc: "Small social missteps replay for hours or days.",
        href: "/relationships-social/fear-of-rejection",
      },
      {
        label: "Performance anxiety",
        desc: "Heart racing before a presentation, a date, or a meeting.",
        href: "/work-performance/workplace-panic",
      },
      {
        label: "Safety behaviors",
        desc: "Carrying water, keeping eyes down, drinking before events.",
        href: "/panic-nervous-system/hypervigilance",
      },
      {
        label: "Post-event rumination",
        desc: "Replaying what you said, looking for evidence of judgment that may not exist.",
        href: "/overthinking-rumination/replaying-conversations",
      },
      {
        label: "Selective mutism (in children)",
        desc: "The most extreme end of the cluster, in children.",
        href: "/conditions/pediatric-anxiety",
      },
    ],
    whyClusterMatters:
      "Social anxiety's cluster is treatable, and the treatment is specific: CBT with exposure, sometimes paired with an SSRI. The single biggest predictor of getting better is treatment that targets the avoidance, not the self-consciousness. Avoidance feels protective in the moment and is the part that keeps the cluster alive.",
    networkTopics: ["social-anxiety-disorder", "anxiety"],
    previewDesc:
      "Avoidance, embarrassment, self-consciousness, anticipatory dread, and the long after-event replay.",
    treatment:
      "Social anxiety responds to exposure-based cognitive behavioral therapy (gradually re-entering the social situations the nervous system has learned to fear) combined when needed with an SSRI. Avoidance is the engine of social anxiety, so any treatment that lets avoidance continue isn't really treatment. The work is uncomfortable, but it's also reliably effective.",
    whatPeopleDescribe: [
      "One person describes the post-event replay. They get home from a dinner, brush their teeth, get into bed, and then the recording starts. Did the comment about work land wrong? Was the joke too much? They re-watch the conversation from every angle, and every angle suggests they did something embarrassing. The replay can run for hours.",
      "Another describes the anticipatory dread the day before a wedding they've already committed to. They draft texts to cancel. They run through every possible interaction. By the time they arrive, they're already exhausted from the rehearsal, and the actual event, when it happens, is fine. The dread, not the wedding, was the cluster.",
    ],
  },
  {
    slug: "sleep-and-anxiety",
    h1: "The Sleep & Anxiety Map",
    name: "Sleep & Anxiety Map",
    central: "Sleep and anxiety",
    pageTitle: "Sleep & Anxiety Map | AnxietyResource",
    metaDescription:
      "A visual map of how anxiety and sleep loop together: the 3 a.m. wake-up, racing pre-sleep thoughts, cortisol, screens, sleep effort, and what actually helps. Psychiatrist-reviewed.",
    subhead:
      "How anxiety and sleep loop into each other at night, and what helps without making the loop tighter.",
    lede: [
      "The 3 a.m. wake-up is its own cluster. So is the racing mind that arrives the moment you lie down. Anxiety and sleep affect each other in both directions: poor sleep makes anxiety louder; anxiety makes sleep harder. The places where the two cluster together are predictable, and so are the things that help and the things that backfire.",
    ],
    nodes: [
      {
        label: "Pre-sleep racing thoughts",
        desc: "The mind speeds up just as the body slows down.",
        href: "/symptoms/racing-thoughts",
      },
      {
        label: "3 a.m. wake-up",
        desc: "Waking around 3 to 4 a.m. with a flood of worry.",
        href: "/symptoms/insomnia",
      },
      {
        label: "Cortisol cycle",
        desc: "The hormonal pattern that pulls you out of sleep before dawn.",
        href: "/glossary/cortisol",
      },
      {
        label: "Bedtime dread",
        desc: "Dreading sleep because you remember last night.",
        href: "/resources/anxiety-keeping-you-up-at-night",
      },
      {
        label: "Sleep effort",
        desc: "Trying to fall asleep is itself a form of arousal; the harder you try, the worse it gets.",
        href: "/glossary/insomnia",
      },
      {
        label: "Screens and sleep",
        desc: "Late-night scrolling and the way it delays both onset and depth.",
        href: "https://quitscrolling.app",
        external: true,
      },
      {
        label: "Sleep hygiene",
        desc: "The basics that actually help, separated from the ones that don't.",
        href: "/glossary/sleep-deprivation",
      },
      {
        label: "CBT-I",
        desc: "Cognitive behavioral therapy for insomnia, which is more effective than medication long-term.",
        href: "/treatment-education/how-cbt-and-ssris-work",
      },
    ],
    whyClusterMatters:
      "The sleep-anxiety loop runs in both directions, which is why fixing one half is rarely enough. CBT-I is the most effective treatment because it works on both halves of the loop at once. Sleep medications can help short-term but most have a tolerance curve that makes them less useful over time. The cluster gets quieter when treatment targets the loop, not the symptom.",
    networkTopics: ["sleep-trouble", "insomnia", "anxiety"],
    previewDesc:
      "The 3 a.m. wake-up, pre-sleep racing thoughts, cortisol cycling, and the wind-down rituals that actually help.",
    treatment:
      "The sleep-anxiety loop responds to cognitive behavioral therapy for insomnia (CBT-I), which is the first-line treatment for chronic insomnia and one of the best-evidenced behavioral treatments in mental health. SSRIs help when anxiety is the upstream cause. Sleep medication has a role short-term but doesn't address the loop.",
    whatPeopleDescribe: [
      "One person describes the 3 a.m. wake-up. They open their eyes, the brain switches on instantly, and the day's worries are queued up and waiting. They reach for the phone, scroll for thirty minutes, set it down, lie there. Sleep comes back around 5. The alarm goes off at 6:30. The next day is harder, and the next night is harder still.",
      "Another describes the pre-sleep racing thoughts. They're physically tired. The body is ready. But the moment the lights are off, the mind starts reviewing the day, then planning tomorrow, then catastrophizing next week. Sleep becomes the thing they're trying to do, and trying is the opposite of falling asleep.",
    ],
  },
  {
    slug: "high-performer",
    h1: "The High-Performer Map",
    name: "High-Performer Map",
    central: "High performer",
    pageTitle: "High-Performer Map | AnxietyResource",
    metaDescription:
      "A visual map of high-functioning anxiety: perfectionism, people-pleasing, imposter syndrome, burnout drift, performance anxiety, and the scoreboard that never reads enough. Psychiatrist-reviewed.",
    subhead:
      "The cluster of patterns that make anxiety hide inside high performance, and the cost they take.",
    lede: [
      "High-functioning anxiety is a specific cluster: a person whose anxiety doesn't show up as decline. It shows up as relentless competence with a scoreboard that never reads enough. The cost lives in the body and in relationships. The map below is what that cluster looks like from inside.",
    ],
    nodes: [
      {
        label: "Perfectionism",
        desc: "Internal standards calibrated higher than any outside person would set.",
        href: "/work-performance/perfectionism",
      },
      {
        label: "People-pleasing",
        desc: "Saying yes when the answer is no, repeatedly.",
        href: "/relationships-social/fear-of-rejection",
      },
      {
        label: "Imposter syndrome",
        desc: "The feeling that competence is a fluke about to be discovered.",
        href: "/work-performance/high-functioning-anxiety",
      },
      {
        label: "Burnout drift",
        desc: "The slow loss of energy, interest, and recovery, without one obvious cause.",
        href: "/work-performance/burnout-vs-anxiety",
      },
      {
        label: "Productivity as identity",
        desc: "Tying your sense of self to output.",
        href: "/glossary/burnout",
      },
      {
        label: "Performance anxiety",
        desc: "The body's anxiety response showing up before a presentation, a meeting, a high-stakes moment.",
        href: "/work-performance/workplace-panic",
      },
      {
        label: "Sleep compromise",
        desc: "Trading sleep for productivity, repeatedly.",
        href: "/symptoms/insomnia",
      },
      {
        label: "High-functioning depression",
        desc: "When the cluster outlives anxiety and shifts into something heavier.",
        href: "/conditions/anxiety-and-depression",
      },
    ],
    whyClusterMatters:
      "The high-performer cluster is one of the most under-treated patterns in modern psychiatry because it doesn't look like a problem from outside. Treatment that works targets the cluster as a whole, usually with therapy on the relationship to performance and the scoreboard, sometimes with medication. shrinQ's Phase 4 is built around this exact pattern.",
    networkTopics: ["high-performer-pattern", "work-and-performance", "burnout", "anxiety"],
    previewDesc:
      "Perfectionism, burnout, imposter syndrome, performance anxiety, and the scoreboard that never reads enough.",
    treatment:
      "The high-performer cluster usually responds to therapy that surfaces the underlying belief structure (that worth is contingent on output) alongside cognitive behavioral skills for the anxiety and perfectionism layered on top. Many high-performers also benefit from an SSRI when anxiety has been running at high baseline for years. The work is rarely about doing less; it's about decoupling worth from output.",
    whatPeopleDescribe: [
      "One person describes the way the scoreboard never resets. They got the promotion, and the relief lasted two hours. By the end of the day, the next target was already in place. The reward of the win was always smaller than the dread of the next miss. Externally, this looked like discipline. Internally, it was running.",
      "Another describes the imposter sensation in meetings full of senior people. They prepare twice as much as anyone else. They speak last and only when sure. Afterward, they review every sentence. The colleagues see someone competent and steady. The person inside is one bad question away from being exposed.",
    ],
  },
  {
    slug: "health-anxiety",
    h1: "The Health Anxiety Map",
    name: "Health Anxiety Map",
    central: "Health anxiety",
    pageTitle: "Health Anxiety Map | AnxietyResource",
    metaDescription:
      "A visual map of health anxiety: body scanning, symptom checking, reassurance loops, sensitization, doctor avoidance, and the cluster's specific treatment. Psychiatrist-reviewed.",
    subhead:
      "The cluster of body-vigilance, symptom-checking, and reassurance loops that turn health worry into a full pattern.",
    lede: [
      "Health anxiety has its own cluster. A body sensation arrives, the mind reads it as a serious illness, the person scans for confirming or disconfirming signs, asks a doctor or a partner or the internet for reassurance, and finds relief that doesn't last. The cluster is treatable, and treatment is specific.",
    ],
    nodes: [
      {
        label: "Body scanning",
        desc: "Repeatedly checking the body for symptoms of illness.",
        href: "/panic-nervous-system/hypervigilance",
      },
      {
        label: "Symptom checking",
        desc: "Searching online for what a sensation could mean.",
        href: "/symptoms/is-my-anxiety-normal",
      },
      {
        label: "Reassurance loops",
        desc: "Asking doctors, partners, search engines for relief that fades within hours.",
        href: "/overthinking-rumination/reassurance-seeking",
      },
      {
        label: "Sensitization",
        desc: "Past health scares make the body more reactive to ordinary sensations.",
        href: "/panic-nervous-system/sensitization",
      },
      {
        label: "Doctor avoidance",
        desc: "The opposite end of the spectrum: avoiding medical care for fear of bad news.",
        href: "/glossary/avoidance",
      },
      {
        label: "Hypochondriacal worry",
        desc: "The persistent belief that a serious illness is being missed.",
        href: "/conditions/obsessive-compulsive-disorder",
      },
      {
        label: "Worry about loved ones' health",
        desc: "The same pattern, directed outward.",
        href: "/conditions/generalized-anxiety-disorder",
      },
      {
        label: "Safety behaviors",
        desc: "Wearable health trackers, repeated tests, supplements.",
        href: "/glossary/coping-mechanisms",
      },
    ],
    whyClusterMatters:
      "Health anxiety sits between OCD and generalized anxiety. The treatment is specific: cognitive behavioral therapy that targets the reassurance loop, sometimes with an SSRI. Reassurance feels protective in the moment and is the part that keeps the cluster running. The map is the cluster; the treatment works on the cluster.",
    networkTopics: ["health-anxiety", "anxiety", "reassurance-seeking"],
    previewDesc:
      "Body scanning, symptom checking, reassurance loops, sensitization, and the slow narrowing of life around perceived risk.",
    treatment:
      "Health anxiety responds best to cognitive behavioral therapy that targets the reassurance loop and body scanning specifically (exposure to uncertainty about health rather than additional certainty about it) combined when needed with an SSRI. Every additional medical test the cluster generates is part of the cluster; the work is learning to tolerate not-knowing.",
    whatPeopleDescribe: [
      "One person describes the cycle: a sensation in the chest, a Google search, a list of possible causes, the worst possible cause feels most likely, an appointment, an ECG, normal results, two days of relief, a new sensation. The reassurance from each clean test is real and short. The cluster repaints the sensation onto a new disease.",
      "Another describes the slow narrowing. They stop drinking caffeine, then stop drinking alcohol, then stop strenuous exercise, then stop eating most foods. Each removal is meant to protect against a possible illness. The world gets smaller. The anxiety doesn't get smaller; it just runs out of places to land.",
    ],
  },
  {
    slug: "generalized-anxiety",
    h1: "The Generalized Anxiety Map",
    name: "Generalized Anxiety Map",
    central: "Generalized Anxiety",
    pageTitle: "Generalized Anxiety Map | AnxietyResource",
    metaDescription:
      "Generalized anxiety isn't one symptom. It's a cluster: worry, tension, hypervigilance, sleep trouble, concentration trouble. See how they connect.",
    subhead:
      "The worry-and-tension pattern that runs in the background of everyday life.",
    lede: [
      "Generalized anxiety doesn't show up as a single dramatic event. It shows up as a low hum that's always on. The worry isn't about one thing; it moves from one thing to the next. The body is tense without an obvious reason. Sleep is shallow. Concentration breaks. The mind is busy, but not productive.",
      "People with this pattern often describe feeling like they can't fully relax, even on a calm day. The nervous system has learned to stay slightly elevated as a baseline, and that elevation runs the show.",
      "This map lays out the experiences that travel together in generalized anxiety. Once you can see them as one cluster, the work shifts from chasing each symptom separately to working with the pattern as a whole.",
    ],
    nodes: [
      { label: "Chronic worry", desc: "Worry that moves from topic to topic without ever settling or resolving.", href: "/conditions/generalized-anxiety-disorder" },
      { label: "Muscle tension", desc: "A body held tight without an obvious reason, often in the jaw, neck, and shoulders.", href: "/glossary/muscle-tension" },
      { label: "Hypervigilance", desc: "A nervous system scanning for threat even when nothing is wrong.", href: "/panic-nervous-system/hypervigilance" },
      { label: "Sleep trouble", desc: "Shallow, interrupted sleep that doesn't fully restore.", href: "/symptoms/insomnia" },
      { label: "Concentration trouble", desc: "A busy mind that struggles to hold focus on one thing.", href: "/glossary/brain-fog" },
      { label: "What-if loops", desc: "Chains of what-if questions that spawn faster than they can be answered.", href: "/overthinking-rumination/catastrophic-thinking" },
      { label: "Restlessness", desc: "A physical inability to settle, as if the body is waiting for something.", href: "/glossary/restlessness" },
    ],
    whyClusterMatters:
      "The generalized anxiety pattern is the most common of all anxiety patterns and the easiest to underestimate, because it doesn't have a single dramatic feature. It's not a panic attack. It's not a phobia. It's the slow, steady wearing down of a nervous system that never gets to fully stand down. Treating the cluster as a whole, instead of the loudest symptom in any given week, is what moves it.",
    networkTopics: ["generalized-anxiety-disorder", "anxiety", "worry"],
    previewDesc:
      "Chronic worry, muscle tension, hypervigilance, sleep disruption, and the body that never quite turns off.",
    treatment:
      "Generalized anxiety responds to cognitive behavioral therapy that targets worry as a behavior, combined when needed with an SSRI or SNRI to lower the baseline tension. Because the cluster is diffuse rather than focused, treatment is usually a steady, multi-track effort: sleep, exercise, therapy, and often medication, all working in the same direction.",
    whatPeopleDescribe: [
      "One person describes the way their shoulders are always slightly raised. They notice it in the mirror sometimes. They drop them deliberately, and within an hour they're back up. The tension isn't about anything specific, it's a setting the body has gotten stuck on.",
      "Another describes the worry loop. The worry rotates: bills, then a parent's health, then a project deadline, then whether they locked the door. None of it stays long enough to solve, and none of it stays absent long enough to rest. By evening they're exhausted, but they can't name what they were doing.",
    ],
  },
  {
    slug: "ocd",
    h1: "The OCD Map",
    name: "OCD Map",
    central: "OCD",
    pageTitle: "OCD Map | AnxietyResource",
    metaDescription:
      "OCD is a loop, not a content theme. See the cluster of experiences inside it: obsessions, compulsions, rituals, avoidance, reassurance, laid out together.",
    subhead:
      "The obsession-compulsion loop, and the experiences that travel inside it.",
    lede: [
      "OCD is often misunderstood as being about neatness or order. It isn't. It's about a loop. An intrusive thought lands. The thought creates discomfort. The discomfort drives a behavior (checking, washing, counting, asking for reassurance, mentally reviewing) that briefly relieves the discomfort. The relief teaches the brain that the loop is necessary. The loop runs again.",
      "The loop can be about almost anything: contamination, harm, relationships, sexual orientation, religion, just-right sensations, mental purity. The content varies. The mechanism doesn't.",
      "This map shows the cluster of experiences that travel together inside OCD. Recognizing the cluster is the start of changing how you respond to it, which is the work that actually moves OCD.",
    ],
    nodes: [
      { label: "Intrusive thoughts", desc: "Unwanted thoughts the person knows aren't them, but that feel urgent anyway.", href: "/overthinking-rumination/intrusive-thoughts" },
      { label: "Compulsions", desc: "Repeated actions done to relieve the discomfort an obsession creates.", href: "/conditions/obsessive-compulsive-disorder" },
      { label: "Mental rituals", desc: "Silent reviewing, counting, or praying done to neutralize a thought.", href: "/conditions/obsessive-compulsive-disorder" },
      { label: "Avoidance", desc: "Steering around the triggers that set the loop running.", href: "/glossary/avoidance" },
      { label: "Reassurance loops", desc: "Asking, checking, and searching for a certainty that never lasts.", href: "/overthinking-rumination/reassurance-seeking" },
      { label: "Just-right sensations", desc: "The pull to repeat something until it feels exactly right.", href: "/conditions/obsessive-compulsive-disorder" },
      { label: "Magical thinking", desc: "The sense that a thought or action can prevent an unrelated harm.", href: "/glossary/cognitive-distortions" },
    ],
    whyClusterMatters:
      "The content of OCD obsessions varies enormously from person to person, but the loop is identical: intrusive thought to distress to compulsion to relief to reinforcement. People with OCD often spend years treating each new content theme as a new problem. It isn't. It's the same loop pointed at a new target. Working with the loop itself, not the content, is what changes OCD.",
    networkTopics: ["obsessive-compulsive-disorder", "intrusive-thoughts", "anxiety"],
    previewDesc:
      "Intrusive thoughts, compulsions, mental rituals, avoidance, reassurance loops, and the just-right feeling that's never quite right.",
    treatment:
      "OCD responds best to a specific kind of cognitive behavioral therapy called exposure and response prevention (ERP), often combined with an SSRI at a higher dose than is typically used for depression. ERP is uncomfortable but reliably effective because it directly breaks the loop. Ordinary talk therapy and reassurance, while well-intentioned, often make OCD worse.",
    whatPeopleDescribe: [
      "One person describes the contamination loop. They wash their hands after touching the trash. The hands feel clean for a moment. Then a doubt arrives, did they touch the trash can with the back of their wrist? They wash again. The relief gets shorter each time. By the end of the day their hands are raw.",
      "Another describes the harm intrusion. Driving home, the thought arrives that they might lose control of the wheel and hurt someone. They don't want this. They know it's not them. But the thought feels dangerous, so they avoid driving alone. The avoidance protects them from the thought briefly. The thought comes back, larger, the next time.",
    ],
  },
  {
    slug: "trauma",
    h1: "The Trauma Map",
    name: "Trauma Map",
    central: "Trauma",
    pageTitle: "Trauma Map | AnxietyResource",
    metaDescription:
      "Trauma is a cluster, hyperarousal, intrusive memories, avoidance, numbing, triggers, sleep disturbance, that often looks like anxiety but runs on a different engine.",
    subhead:
      "The post-traumatic cluster: hyperarousal, intrusive memories, avoidance, and the body that's still on guard.",
    lede: [
      "Trauma doesn't stay in the past. The mind moves on, but the body often doesn't. A nervous system that learned a specific event was dangerous keeps treating echoes of that event, a smell, a tone of voice, a time of day, as the same danger. The result is a cluster of experiences that often look like anxiety but are running on a different engine.",
      "The cluster includes things that don't seem to belong together: a hair-trigger startle response, a numbness that comes and goes, sleep that doesn't restore, memories that arrive uninvited, and an avoidance pattern that quietly shrinks daily life.",
      "This map shows those experiences as one cluster. Seeing them together is often the moment when 'I have a lot of weird symptoms' becomes 'this is one pattern, and there's a name for it, and there's a way to work with it.'",
    ],
    nodes: [
      { label: "Hyperarousal", desc: "A nervous system stuck in high alert, startling easily and slow to settle.", href: "/panic-nervous-system/fight-or-flight" },
      { label: "Intrusive memories", desc: "Memories of the event that arrive uninvited, in images or sensations.", href: "/conditions/post-traumatic-stress-disorder" },
      { label: "Avoidance", desc: "Steering around the people, places, and reminders tied to the event.", href: "/glossary/avoidance" },
      { label: "Emotional numbing", desc: "A flatness or distance that comes and goes, muting both pain and pleasure.", href: "/conditions/post-traumatic-stress-disorder" },
      { label: "Triggers", desc: "Ordinary cues, a smell, a sound, a time of day, that the body reads as danger.", href: "/panic-nervous-system/sensitization" },
      { label: "Sleep disturbance", desc: "Sleep that doesn't restore, often interrupted by waking in the night.", href: "/symptoms/insomnia" },
      { label: "Hypervigilance", desc: "Constant scanning for threat, as if the danger could return at any moment.", href: "/panic-nervous-system/hypervigilance" },
    ],
    whyClusterMatters:
      "The post-traumatic cluster often looks like anxiety, but it isn't quite anxiety, it's anxiety pointing at a specific past event the nervous system never finished processing. Treating it as ordinary anxiety usually doesn't work. Treating it as a trauma cluster, with trauma-specific approaches, usually does.",
    networkTopics: ["post-traumatic-stress-disorder", "trauma", "anxiety"],
    previewDesc:
      "Hyperarousal, intrusive memories, avoidance, numbing, triggers, sleep disturbance, and the body that learned the threat isn't over.",
    treatment:
      "The post-traumatic cluster responds to trauma-specific therapies: prolonged exposure (PE), cognitive processing therapy (CPT), and EMDR are the most studied and most effective. SSRIs can help with sleep, hyperarousal, and mood. Trauma rarely responds to generic anxiety treatment because the underlying mechanism is different.",
    whatPeopleDescribe: [
      "One person describes the body's reaction to a specific cologne. Years after the event that involved someone wearing it, the smell still triggers a wave of nausea, racing heart, and the urge to leave the room. They know intellectually the smell isn't dangerous. The body doesn't.",
      "Another describes the way sleep changed after the event. They fall asleep fine. They wake up at 2 a.m., heart racing, with no memory of a dream. They lie in the dark waiting for it to pass. It always passes. It also always comes back.",
    ],
  },
];

export function getMap(slug: string): AnxietyMapData | undefined {
  return MAPS.find((m) => m.slug === slug);
}

export function mapAltText(map: AnxietyMapData): string {
  return altFor(map);
}
